package org.teddapp.project.sync

fun interface Clock { fun now(): Long }

data class RemoteRow(
    val id: String,
    val updatedAt: Long,
    val isDeleted: Boolean,
    val deletedAt: Long?,
    val payload: Map<String, Any?>,
    /** Numéro d'arrivée attribué par le serveur ; null tant que le serveur n'en donne pas. */
    val receivedSeq: Long? = null,
)

/**
 * Un curseur plus grand que ceci est une date en millisecondes, héritée de l'ancien
 * moteur ; un numéro d'arrivée n'atteindra jamais cette valeur. On repart de zéro.
 */
const val LEGACY_CURSOR_THRESHOLD: Long = 100_000_000_000L

sealed interface SyncResult {
    data object Success : SyncResult

    /**
     * Au moins une table n'a pas pu être poussée ou tirée. Les autres l'ont été : un
     * échec ne bloque plus le cycle, il le rend partiel. [entity] et [cause] sont ceux
     * du premier échec ; [summary] les nomme tous, pour l'écran.
     */
    data class Failure(
        val entity: EntityKind?,
        val cause: Throwable,
        val summary: String? = null,
        /** Chaque table en échec, avec son nombre de lignes et la cause : pour le tableau de bord. */
        val failures: List<SyncEntityFailure> = emptyList(),
    ) : SyncResult
}

/** Une table qui n'a pas pu être poussée ou tirée pendant un cycle. */
data class SyncEntityFailure(val entity: EntityKind, val rows: Int, val message: String)

enum class EntityKind(val tableName: String) {
    UserProfile("user_profile"),
    Organization("organization"),
    Category("category"),
    FinanceAccount("finance_account"),
    FinanceCategory("finance_category"),
    PomodoroSession("pomodoro_session"),
    ExpenseList("expense_list"),
    FocusSession("focus_session"),
    Objective("objective"),
    Project("project"),
    ProjectStatus("projectStatus"),
    Milestone("milestone"),
    Task("task"),
    FinanceTransaction("finance_transaction"),
    ExpenseListItem("expense_list_item"),
    PomodoroItem("pomodoro_item"),
    PomodoroCheck("pomodoro_check"),
    Notebook("notebook"),
    Note("note"),
    Skill("skill"),
    ParcoursStep("parcours_step"),
    Asset("asset"),
    AssetValuation("asset_valuation"),
    Flashcard("flashcard"),
    ObjectiveReflection("objective_reflection"),
    CravingObservation("craving_observation"),
    TimerState("timer_state"),
    CompetenceFamily("competence_family"),
    Competence("competence"),
    CompetenceCriterion("competence_criterion"),
    PortfolioPiece("portfolio_piece"),
    PortfolioPieceCompetence("portfolio_piece_competence"),
    MindMap("mind_map"),
    MindMapNode("mind_map_node"),
    MindMapNodeNote("mind_map_node_note"),
    Attachment("attachment"),
    Keychain("keychain"),
    KeychainItem("keychain_item"),
    ExpenseEntry("expense_entry"),
    Simulation("simulation"),
    SimulationLine("simulation_line"),
    Loan("loan"),
    LoanPayment("loan_payment"),
    SkillResource("skill_resource"),
    AssistantConversation("assistant_conversation"),
    AssistantMessage("assistant_message"),
    ;

    companion object {
        val pushOrder: List<EntityKind> = listOf(
            UserProfile,
            Organization,
            Category,
            FinanceAccount,
            FinanceCategory,
            PomodoroSession,
            ExpenseList,
            FocusSession,
            Objective,
            Project,
            ProjectStatus,
            Milestone,
            Task,
            FinanceTransaction,
            ExpenseListItem,
            // Après sa ligne : le serveur refuse une sous-dépense dont la ligne est inconnue.
            ExpenseEntry,
            // Le brouillon avant ses lignes.
            Simulation,
            SimulationLine,
            // Après les comptes ; le prêt avant ses remboursements.
            Loan,
            LoanPayment,
            PomodoroItem,
            PomodoroCheck,
            Notebook,
            Skill,
            ParcoursStep,
            // Après sa compétence et son étape, avant son fichier : le serveur refuse un
            // fichier dont la ressource est inconnue.
            SkillResource,
            // Après sa compétence ; ses messages après elle, pour qu'ils la trouvent au pull.
            AssistantConversation,
            AssistantMessage,
            CompetenceFamily,
            Competence,
            CompetenceCriterion,
            PortfolioPiece,
            PortfolioPieceCompetence,
            // Les nœuds avant les notes (une note peut vivre dans un nœud), les liens après.
            MindMap,
            MindMapNode,
            Note,
            MindMapNodeNote,
            // Après les notes : le serveur refuse un fichier dont la note est inconnue.
            Attachment,
            // Le trousseau avant ses entrées : le serveur refuse une entrée sans trousseau.
            Keychain,
            KeychainItem,
            Asset,
            AssetValuation,
            Flashcard,
            ObjectiveReflection,
            CravingObservation,
            TimerState,
        )

        val pullOrder: List<EntityKind> = pushOrder
    }
}
